import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAdmin } from '../../contexts/AdminContext';
import { useAuth } from '../../contexts/AuthContext';
import { useCart } from '../../contexts/CartContext';
import { useSupabaseData } from '../../contexts/SupabaseDataContext';
import { productApi } from '../../lib/supabase-api';
import { Product, ProductVariant, getProductCategory, getProductPrice, getProductOriginalPrice, getProductDiscount } from '../../types';
import QuantityPriceSelector from '../common/QuantityPriceSelector';
import MarkdownRenderer from '../common/MarkdownRenderer';
import SEOHead from '../common/SEOHead';
import ProductDescription from '../product/ProductDescription';
import RelatedProducts from '../product/RelatedProducts';
import SubstitutionNotice from '../common/SubstitutionNotice';
import { getProductSEO } from '../../utils/seo';
import { getProductFaqs } from '../../data/productFaqs';
import AuthorByline from '../common/AuthorByline';
import { useToast } from '../ui/Toast';

interface ProductDetailProps {
  products?: Product[];
}

interface SelectedOptions {
  [optionName: string]: string;
}

// Derive the singular unit label (the item inside a container) from the product name
// e.g. "Valif Oral Jelly 20 Mg" → "Sachet"
//      "Cenforce 100 Mg Capsule" → "Capsule"
//      "Semasize 1 Mg Pen" → "Pen"
//      "Hiforce 100 ODS" → "Strip"
//      Default → "Tablet"
const deriveUnitLabel = (name: string, strengthLabel?: string, categories?: string[]): string => {
  const n = name.toLowerCase();
  const s = (strengthLabel || '').toLowerCase();
  const cats = (categories || []).map(c => c.toLowerCase());

  if (n.includes('oral jelly') || n.includes('sachet')) return 'Sachet';
  if (n.includes('inhaler')) return 'Inhaler';
  if (n.includes('pen')) return 'Pen';
  if (n.includes('injection') || n.includes('vial') || n.includes('ampoule')) return 'Vial';
  if (n.includes('spray')) return 'Spray';
  if (n.includes('cream') || n.includes('ointment') || n.includes('gel')) return 'Tube';
  if (n.includes('drops')) return 'Bottle';
  if (n.includes('solution') || n.includes('syrup') || n.includes('suspension')) return 'Bottle';
  if (n.includes('capsule')) return 'Capsule';
  if (n.includes('ods') || n.includes('strip')) return 'Strip';
  if (n.includes('powder')) return 'Sachet';

  // Topical retinoids by brand/ingredient — all tretinoin/trifarotene products
  // are creams or gels, even if the product name doesn't explicitly say so
  if (n.includes('tretinoin') || n.includes('trifarotene') || n.includes('retin-a') ||
      n.includes('skyntret') || n.includes('tretiheal') || n.includes('aklife') ||
      cats.includes('retinol')) {
    return 'Tube';
  }

  // Topical minoxidil (5%, 10% solutions) vs oral minoxidil tablets
  // The minoxidil tablet variants have "mg" in their strength label ("5 Mg Tablet"),
  // topical solutions have "%" or nothing
  if (n.includes('minoxidil') || n.includes('minoxi')) {
    if (s.includes('mg') || s.includes('tablet')) return 'Tablet';
    return 'Bottle';
  }

  // Oral semaglutide uses tablets; injectable pen already matched above
  return 'Tablet';
};

// Container label: the outer pack. Oral jelly uses "Week" (1 pack = 7 sachets
// = 1 week supply), inhalers and pens are single-unit containers.
const deriveContainerLabel = (name: string): string => {
  const n = name.toLowerCase();
  if (n.includes('oral jelly')) return 'Week';
  if (n.includes('inhaler')) return 'Inhaler';
  return 'Box';
};

// GLP-1 pens with different pack structures need different suffix math:
//   • Semasize     — 1 box = 1 multi-dose pen = 4 weekly doses; pen name's mg = weekly dose
//   • Noveltreat   — 1 box = 2 single-dose pens = 2 weekly doses; pen name's mg = weekly dose
//   • Obeda        — 1 box = 1 multi-dose pen = 4 weekly doses; pen name's mg = TOTAL content
//                    (so weekly = total / 4: Obeda 2 Mg → 0.5 mg/week, Obeda 4 Mg → 1 mg/week)
const deriveWeeklyDoseSuffix = (name: string): ((qty: number) => string) | undefined => {
  const semasize = name.match(/Semasize(?:\s+Plus)?\s+(\d+(?:\.\d+)?)\s*Mg\s+Pen/i);
  if (semasize) {
    const mg = semasize[1];
    return (qty: number) => ` · ${qty * 4}-week supply (${mg} mg/week)`;
  }
  const noveltreat = name.match(/Noveltreat\s+(\d+(?:\.\d+)?)\s*Mg\s+Pen/i);
  if (noveltreat) {
    const mg = noveltreat[1];
    return (qty: number) => ` · ${qty * 2}-week supply (${mg} mg/week)`;
  }
  const obeda = name.match(/Obeda\s+(\d+(?:\.\d+)?)\s*Mg\s+Pen/i);
  if (obeda) {
    const weekly = parseFloat(obeda[1]) / 4;
    return (qty: number) => ` · ${qty * 4}-week supply (${weekly} mg/week)`;
  }
  return undefined;
};


const ProductDetail: React.FC<ProductDetailProps> = ({ products: legacyProducts }) => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { isLoggedIn } = useAuth();
  const { products: supabaseProducts } = useSupabaseData();
  const { reviews, isLoading } = useAdmin();
  const { addToCart, addToWishlist, removeFromWishlist, isInWishlist } = useCart();

  const products: Product[] = useMemo(() => {
    if (supabaseProducts.length > 0) {
      return supabaseProducts;
    }
    return legacyProducts || [];
  }, [supabaseProducts, legacyProducts]);
  const [quantity, setQuantity] = useState(1);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [currentPrice, setCurrentPrice] = useState(0);
  const [currentQuantity, setCurrentQuantity] = useState(1);
  const [selectedOptions, setSelectedOptions] = useState<SelectedOptions>({});
  const [activeTab, setActiveTab] = useState('info');
  const [selectedVariantId, setSelectedVariantId] = useState<string | null>(null);
  const [fullProduct, setFullProduct] = useState<Product | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const slimProduct = products.find(p => p.id === productId);

  // Lazy-load full product details (description, ingredients, etc.)
  // getAll() excludes heavy text fields to save egress bandwidth.
  // Re-fetches when productId changes or when realtime pushes a newer updated_at.
  const slimUpdatedAt = slimProduct?.updated_at;
  useEffect(() => {
    if (!productId) return;
    let cancelled = false;
    setDetailLoading(true);
    setFullProduct(null);
    productApi.getById(productId)
      .then(full => {
        if (!cancelled && full) setFullProduct(full);
      })
      .catch(err => { console.error('Failed to load product details:', err); })
      .finally(() => { if (!cancelled) setDetailLoading(false); });
    return () => { cancelled = true; };
  }, [productId, slimUpdatedAt]);

  const product = fullProduct ?? slimProduct;

  // Sorted variants (by strength_mg ascending) — what the selector shows
  const variants: ProductVariant[] = useMemo(() => {
    if (!product?.variants) return [];
    return [...product.variants].sort((a, b) => {
      if (a.display_order !== b.display_order) return a.display_order - b.display_order;
      return (a.strength_mg ?? 0) - (b.strength_mg ?? 0);
    });
  }, [product]);

  // Currently selected variant (defaults to is_default or first)
  const selectedVariant: ProductVariant | null = useMemo(() => {
    if (variants.length === 0) return null;
    if (selectedVariantId) {
      const match = variants.find(v => v.id === selectedVariantId);
      if (match) return match;
    }
    return variants.find(v => v.is_default) || variants[0];
  }, [variants, selectedVariantId]);

  // Whether to render the strength selector UI (hide for 1-variant products)
  const hasMultipleVariants = variants.length > 1;
  const hasMeaningfulStrengths = variants.some(v => v.strength_mg != null);
  const showVariantSelector = hasMultipleVariants && hasMeaningfulStrengths;

  React.useEffect(() => {
    if (currentPrice === 0 && product) {
      setCurrentPrice(getProductPrice(product));
    }
  }, [product, currentPrice]);

  if (isLoading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-warm-200 border-t-forest-800 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-body-sm text-warm-500">Loading...</p>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-display text-display-sm text-forest-900 mb-4">Product not found</h1>
          <Link to="/" className="text-gold-400 hover:underline font-medium">Return to Home</Link>
        </div>
      </div>
    );
  }

  // Main image follows the currently selected variant so that switching strength
  // (e.g. Suhagra 25mg → 100mg) also updates the photo. Fall back to family head image.
  const currentImage = selectedVariant?.image || product.image;
  const productImages = [currentImage, currentImage, currentImage, currentImage];

  const handleQuantityChange = (change: number) => {
    const newQuantity = quantity + change;
    if (newQuantity >= 1) {
      setQuantity(newQuantity);
    }
  };

  const handleOptionChange = (optionName: string, value: string) => {
    setSelectedOptions(prev => ({
      ...prev,
      [optionName]: value
    }));
  };

  const calculatePrice = () => {
    let basePrice = product.final_price || product.price || 0;
    return basePrice;
  };

  const totalPrice = currentPrice * quantity;

  const handlePriceChange = (price: number, selectedQuantity: number, origPrice: number) => {
    setCurrentPrice(price);
    setCurrentQuantity(selectedQuantity);
  };

  const currentDiscount = product.discount || 0;

  const maskUserName = (userName: string) => {
    if (!userName || userName.length <= 3) return userName;
    const visiblePart = userName.slice(0, -3);
    const maskedPart = '*'.repeat(3);
    return visiblePart + maskedPart;
  };

  const productReviews = reviews.filter(review =>
    review.productId === product.id && (review.status || (review.is_verified ? "approved" : "pending")) === "approved"
  );

  const renderStars = (rating: number) => {
    return [...Array(5)].map((_, i) => (
      <svg
        key={i}
        className={`w-4 h-4 ${
          i < Math.floor(rating) ? 'text-gold-400' : 'text-warm-300'
        }`}
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
      </svg>
    ));
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'info':
        return (
          <section>
            {detailLoading && !fullProduct ? (
              <div className="max-w-4xl mx-auto px-4 sm:px-6 space-y-4 animate-pulse">
                <div className="h-6 bg-warm-200 rounded w-1/3" />
                <div className="h-4 bg-warm-100 rounded w-full" />
                <div className="h-4 bg-warm-100 rounded w-5/6" />
                <div className="h-4 bg-warm-100 rounded w-4/6" />
              </div>
            ) : !detailLoading && !fullProduct ? (
              <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center py-8">
                <p className="text-warm-500 text-body-sm">Product details could not be loaded.</p>
              </div>
            ) : (
              <ProductDescription product={product} />
            )}
          </section>
        );

      case 'reviews':
        return (
          <div className="max-w-4xl mx-auto">
            <h3 className="font-display text-heading-lg text-forest-900 mb-8 text-center tracking-tight">Product Reviews</h3>
            {productReviews.length > 0 ? (
              <div className="space-y-5">
                {productReviews.map((review) => (
                  <div key={review.id} className="bg-cream-50 border border-warm-200 rounded-xl p-6 sm:p-8">
                    <div className="flex items-center space-x-3 mb-5 p-3 bg-cream-100 rounded-xl">
                      <img
                        src={product.image || '/images/products/default.png'}
                        alt={product.name}
                        className="w-12 h-12 object-contain rounded-lg"
                      />
                      <div className="flex-1">
                        <h4 className="text-sm font-medium text-forest-900">{product.name}</h4>
                        <span className="inline-block bg-forest-50 text-forest-700 px-2.5 py-1 rounded-full text-micro font-semibold mt-1">
                          {review.images && review.images.length > 0 ? '$10 credit' : '$5 credit'} earned
                        </span>
                      </div>
                    </div>

                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <div className="flex items-center space-x-2 mb-2">
                          <div className="flex">{renderStars(review.rating)}</div>
                          <span className="text-sm font-medium text-warm-600">{review.rating}/5</span>
                        </div>
                        <p className="text-caption text-warm-500">
                          {maskUserName(review.userName || 'Anonymous')} · {new Date(review.createdAt || review.created_at || new Date()).toLocaleDateString('en-US')}
                        </p>
                      </div>
                    </div>
                    <p className="text-warm-600 leading-relaxed mb-4">{review.content}</p>
                    {review.images && review.images.length > 0 && (
                      <div className="flex space-x-2">
                        {review.images.map((image, index) => (
                          <img
                            key={index}
                            src={image}
                            alt={`Review image ${index + 1}`}
                            className="w-20 h-20 object-cover rounded-xl"
                          />
                        ))}
                      </div>
                    )}
                    {review.adminReply && (
                      <div className="mt-5 p-5 bg-forest-50 border border-forest-200 rounded-xl">
                        <p className="text-caption font-semibold text-forest-800 mb-1">Seller Response</p>
                        <p className="text-body-sm text-forest-700">{review.adminReply}</p>
                      </div>
                    )}
                  </div>
                ))}
                <div className="text-center pt-4">
                  <Link to="/review" className="btn-primary">
                    View All Reviews
                  </Link>
                </div>
              </div>
            ) : (
              <div className="text-center py-16">
                <p className="text-warm-500 mb-6">No reviews yet.</p>
                <Link to="/review/write" className="btn-primary">
                  Write the First Review
                </Link>
              </div>
            )}
          </div>
        );

      case 'shipping':
        return (
          <div className="max-w-4xl mx-auto">
            <h3 className="font-display text-heading-lg text-forest-900 mb-8 text-center tracking-tight">Shipping & Returns</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="bg-cream-100 rounded-xl p-6 sm:p-8">
                <h4 className="font-display text-subheading text-forest-900 mb-4">Shipping</h4>
                <ul className="space-y-3 text-body-sm text-warm-600">
                  <li className="flex gap-3">
                    <span className="w-1 h-1 rounded-full bg-warm-400 mt-2.5 flex-shrink-0" />
                    International shipping typically takes a minimum of 2 weeks.
                  </li>
                  <li className="flex gap-3">
                    <span className="w-1 h-1 rounded-full bg-warm-400 mt-2.5 flex-shrink-0" />
                    Please allow 2-3 business days for order processing.
                  </li>
                  <li className="flex gap-3">
                    <span className="w-1 h-1 rounded-full bg-warm-400 mt-2.5 flex-shrink-0" />
                    We are committed to safe and timely delivery.
                  </li>
                </ul>
              </div>
              <div className="bg-cream-100 rounded-xl p-6 sm:p-8">
                <h4 className="font-display text-subheading text-forest-900 mb-4">Returns & Exchanges</h4>
                <ul className="space-y-3 text-body-sm text-warm-600">
                  <li className="flex gap-3">
                    <span className="w-1 h-1 rounded-full bg-warm-400 mt-2.5 flex-shrink-0" />
                    Free returns for defective or incorrect products.
                  </li>
                  <li className="flex gap-3">
                    <span className="w-1 h-1 rounded-full bg-warm-400 mt-2.5 flex-shrink-0" />
                    Contact customer service within 7 days of delivery.
                  </li>
                  <li className="flex gap-3">
                    <span className="w-1 h-1 rounded-full bg-warm-400 mt-2.5 flex-shrink-0" />
                    Items must be unopened and in original condition.
                  </li>
                </ul>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const handleBuyNow = () => {
    if (!isLoggedIn) {
      toast.warning('Please log in to continue.');
      navigate('/login');
      return;
    }

    const cartProduct = {
      ...product,
      finalPrice: currentPrice,
      pillQuantity: currentQuantity,
      stock: 999
    };
    addToCart(cartProduct, quantity);
    toast.success('Added to cart!');
    navigate('/cart');
  };

  const handleAddToCart = () => {
    const cartProduct = {
      ...product,
      finalPrice: currentPrice,
      pillQuantity: currentQuantity,
      stock: 999
    };
    addToCart(cartProduct, quantity);
    toast.success('Added to cart.');
  };

  const handleWishlistToggle = () => {
    const productId = String(product.id);
    if (isInWishlist(productId)) {
      removeFromWishlist(productId);
      toast.info('Removed from wishlist.');
    } else {
      addToWishlist(productId);
      toast.success('Added to wishlist.');
    }
  };

  const productIdString = String(product.id);
  const productFaqs = getProductFaqs(product);
  const productSEO = getProductSEO(product, productFaqs);

  return (
    <>
      <SEOHead seoData={productSEO} />
      <div className="section-container py-8 lg:py-12">
        {/* Breadcrumb */}
        <nav className="mb-8">
          <ol className="flex items-center gap-2 text-caption text-warm-400">
            <li><Link to="/" className="hover:text-forest-900 transition-colors">Home</Link></li>
            <li className="flex items-center gap-2">
              <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
              </svg>
              <Link to="/shop" className="hover:text-forest-900 transition-colors">Shop</Link>
            </li>
            <li className="flex items-center gap-2">
              <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
              </svg>
              <span className="text-warm-600">{product.category_name}</span>
            </li>
          </ol>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
          {/* Left - Images */}
          <div className="space-y-4">
            <div className="aspect-square bg-cream-100 rounded-2xl overflow-hidden">
              <img
                src={productImages[selectedImageIndex] || ''}
                alt={product.name || ''}
                className="w-full h-full object-contain"
              />
            </div>

            <div className="flex gap-3">
              {productImages.map((image, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedImageIndex(index)}
                  className={`w-20 h-20 border-2 rounded-2xl overflow-hidden transition-all duration-200 ${
                    selectedImageIndex === index ? 'border-forest-800 shadow-card' : 'border-warm-200 hover:border-warm-400'
                  }`}
                >
                  <img
                    src={image || ''}
                    alt={`${product.name || ''} ${index + 1}`}
                    className="w-full h-full object-contain"
                  />
                </button>
              ))}
            </div>
          </div>

          {/* Right - Info */}
          <div className="space-y-6">
            <div>
              <span className="text-nano font-semibold text-warm-400 uppercase tracking-[0.12em]">{product.category_name}</span>
              <h1 className="font-display text-display-sm text-forest-900 mt-2 tracking-tight">{product.name}</h1>
              <div className="flex items-center gap-3 mt-4">
                <div className="flex items-center">
                  {[...Array(5)].map((_, i) => (
                    <svg
                      key={i}
                      className={`w-4 h-4 ${
                        i < Math.floor(product.rating || 0) ? 'text-gold-400' : 'text-warm-200'
                      }`}
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
                <span className="text-caption font-medium text-warm-600">{product.rating}</span>
                <span className="text-caption text-warm-400">({product.review_count || 0} reviews)</span>
              </div>
            </div>

            {/* Strength (variant) selector — only shown for multi-variant products with real mg values */}
            {showVariantSelector && (
              <div className="pt-6 border-t border-warm-200">
                <label className="block text-sm font-medium text-forest-700 mb-3">
                  Select Strength
                </label>
                <div className="flex flex-wrap gap-2">
                  {variants.map(v => {
                    const active = selectedVariant?.id === v.id;
                    return (
                      <button
                        key={v.id}
                        onClick={() => setSelectedVariantId(v.id)}
                        className={`px-4 py-2 rounded-lg border-2 font-medium text-sm transition-all duration-200 ${
                          active
                            ? 'border-forest-800 bg-forest-50 text-forest-900'
                            : 'border-warm-200 bg-cream-50 text-warm-600 hover:border-warm-400'
                        }`}
                      >
                        {v.strength_label || 'Default'}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Quantity price selector (driven by the selected variant's price tiers) */}
            <div className="pt-6 border-t border-warm-200">
              <QuantityPriceSelector
                prices={selectedVariant?.prices || []}
                onPriceChange={handlePriceChange}
                pillQuantity={product.pill_quantity || 0}
                unitLabel={deriveUnitLabel(product.name || '', selectedVariant?.strength_label ?? undefined, (product as any).categories)}
                containerLabel={deriveContainerLabel(product.name || '')}
                labelSuffix={deriveWeeklyDoseSuffix(product.name || '')}
                className=""
              />
            </div>

            {/* Order Quantity & Actions */}
            <div className="pt-6 border-t border-warm-200 space-y-5">
              {(() => {
                const unit = deriveUnitLabel(product.name || '', selectedVariant?.strength_label ?? undefined, (product as any).categories).toLowerCase();
                const container = deriveContainerLabel(product.name || '').toLowerCase();
                const pillQty = product.pill_quantity || 0;
                const pluralUnit = (n: number) => n === 1 ? unit : (unit.endsWith('s') || unit.endsWith('x') ? unit + 'es' : unit + 's');
                const pluralContainer = (n: number) => n === 1 ? container : (container.endsWith('s') || container.endsWith('x') ? container + 'es' : container + 's');
                const per = pillQty > 0 ? pillQty : currentQuantity;
                const totalUnits = per * quantity;
                return (
                  <>
                    <div className="flex items-center justify-between">
                      <span className="text-body-sm font-medium text-warm-600">Quantity:</span>
                      <div className="text-body-sm text-warm-500">
                        {pillQty > 0
                          ? `${per} ${pluralUnit(per)} × ${quantity} ${pluralContainer(quantity)} = ${totalUnits} ${pluralUnit(totalUnits)} total`
                          : `${currentQuantity} ${pluralUnit(currentQuantity)} × ${quantity} order${quantity > 1 ? 's' : ''} = ${currentQuantity * quantity} ${pluralUnit(currentQuantity * quantity)} total`}
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-body-sm font-medium text-warm-600">{pillQty > 0 ? pluralContainer(2) + ':' : 'Orders:'}</span>
                <div className="flex items-center border border-warm-200 rounded-lg overflow-hidden">
                  <button
                    onClick={() => handleQuantityChange(-1)}
                    className="p-2.5 hover:bg-forest-50 disabled:opacity-50 transition-colors"
                    disabled={quantity <= 1}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                    </svg>
                  </button>
                  <span className="px-5 py-2 text-body-sm font-semibold min-w-[3rem] text-center text-forest-900">
                    {quantity}
                  </span>
                  <button
                    onClick={() => handleQuantityChange(1)}
                    className="p-2.5 hover:bg-forest-50 transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                  </button>
                </div>
              </div>
                  </>
                );
              })()}

              {/* Total */}
              <div className="bg-cream-100 rounded-xl p-5 border border-warm-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-body-sm text-warm-500 font-medium">Total Price</span>
                  {getProductDiscount(product) > 0 && (
                    <span className="bg-gold-400 text-forest-900 text-nano px-3 py-1 rounded-full font-semibold">
                      {getProductDiscount(product)}% OFF
                    </span>
                  )}
                </div>
                <div className="flex items-end justify-between">
                  <div>
                    {getProductOriginalPrice(product) > currentPrice && (
                      <div className="text-caption text-warm-400 line-through mb-0.5">
                        ${(getProductOriginalPrice(product) * quantity).toLocaleString()}
                      </div>
                    )}
                    <div className="font-display text-display-sm text-forest-900 tracking-tight">
                      ${totalPrice.toLocaleString()}
                    </div>
                  </div>
                  <div className="text-micro text-warm-400">
                    ${(currentPrice / currentQuantity).toFixed(2)} per pill
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={handleAddToCart}
                  className="border-2 border-forest-800 text-forest-800 hover:bg-forest-50 py-4 px-6 rounded-lg font-semibold transition-all duration-200 flex items-center justify-center gap-2 cursor-pointer text-[15px] active:scale-[0.98]"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.75 10.5V6a3.75 3.75 0 10-7.5 0v4.5m11.356-1.993l1.263 12c.07.665-.45 1.243-1.119 1.243H4.25a1.125 1.125 0 01-1.12-1.243l1.264-12A1.125 1.125 0 015.513 7.5h12.974c.576 0 1.059.435 1.119 1.007zM8.625 10.5a.375.375 0 11-.75 0 .375.375 0 01.75 0zm7.5 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
                  </svg>
                  Add to Cart
                </button>
                <button
                  onClick={handleBuyNow}
                  className="bg-forest-800 hover:bg-forest-700 text-cream-100 py-4 px-6 rounded-lg font-semibold transition-all duration-200 flex items-center justify-center cursor-pointer text-[15px] active:scale-[0.98] border border-forest-700/50"
                >
                  Buy Now
                </button>
              </div>

              {/* Wishlist */}
              <button
                onClick={handleWishlistToggle}
                className={`w-full border-2 py-3.5 px-6 rounded-lg font-medium transition-all flex items-center justify-center gap-2 cursor-pointer text-[14px] ${
                  isInWishlist(productIdString)
                    ? 'border-red-200 text-red-600 bg-red-50 hover:bg-red-100'
                    : 'border-warm-200 text-warm-600 hover:bg-cream-100 hover:border-warm-300'
                }`}
              >
                <svg className="w-5 h-5" fill={isInWishlist(productIdString) ? "currentColor" : "none"} stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
                {isInWishlist(productIdString) ? 'Remove from Wishlist' : 'Add to Wishlist'}
              </button>
            </div>

            {/* Shipping Info */}
            <div className="bg-cream-100 rounded-xl p-5 border border-warm-200">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-cream-50 rounded-xl flex items-center justify-center flex-shrink-0 border border-warm-200">
                  <svg className="w-5 h-5 text-warm-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4-8-4m16 0v10l-8 4-8-4V7m16 0l-8 4-8-4" />
                  </svg>
                </div>
                <div>
                  <div className="text-body-sm font-semibold text-forest-900 mb-1">Estimated Delivery</div>
                  <div className="text-body-sm text-warm-500 leading-relaxed">
                    International shipping typically takes <span className="font-semibold text-forest-900">2+ weeks</span>.
                  </div>
                  <div className="text-micro text-warm-400 mt-1">Direct international shipping</div>
                </div>
              </div>
            </div>

            {/* Substitution fine print */}
            <SubstitutionNotice className="px-1" />
          </div>
        </div>

        {/* Tabs */}
        <div className="mt-16 lg:mt-24">
          <div className="border-b border-warm-200">
            <nav className="-mb-px flex justify-center gap-0">
              {[
                { key: 'info', label: 'Product Info' },
                { key: 'reviews', label: `Reviews (${productReviews.length})` },
                { key: 'shipping', label: 'Shipping & Returns' },
              ].map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`border-b-2 py-4 px-6 sm:px-10 text-[15px] font-medium transition-all duration-200 ${
                    activeTab === tab.key
                      ? 'border-forest-800 text-forest-900'
                      : 'border-transparent text-warm-400 hover:text-forest-700 hover:border-warm-300'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          <div className="py-14 lg:py-16">
            {renderTabContent()}
          </div>

          {productFaqs.length > 0 && (
            <section className="mt-16 border-t border-warm-200 pt-12">
              <h2 className="font-display text-2xl text-forest-900 mb-2">Common questions</h2>
              <p className="text-warm-500 text-sm mb-8">Quick answers for the things people most often ask before ordering.</p>
              <div className="space-y-2">
                {productFaqs.map((faq, idx) => (
                  <details
                    key={faq.question}
                    className="group border border-warm-200 rounded-xl px-5 py-4 hover:border-warm-300 transition-colors"
                    open={idx === 0}
                  >
                    <summary className="cursor-pointer font-medium text-forest-800 flex justify-between items-start list-none gap-4">
                      <span className="flex-1">{faq.question}</span>
                      <span className="text-warm-400 text-xl leading-none mt-0.5 group-open:rotate-45 transition-transform">+</span>
                    </summary>
                    <p className="mt-3 text-sm text-warm-700 leading-relaxed">{faq.answer}</p>
                  </details>
                ))}
              </div>
            </section>
          )}

          <AuthorByline lastUpdated={product.updated_at} />

          {activeTab === 'info' && <RelatedProducts currentProduct={product} />}
        </div>
      </div>
    </>
  );
};

export default ProductDetail;
